import type { FC, FormEventHandler } from 'react';
import React, { useState } from 'react';

import styled from '@emotion/styled';

import { useLazyQuery } from '@apollo/client/react';

import { COLORS, mq } from '@clutter/clean';

import type { PageQuery } from '@graphql/platform';
import { GeocodeDocument } from '@graphql/platform';

import { PageContent, PageSectionSubtitle, PageSectionTitle } from './shared';
import { Arrow } from '@images/svg/arrow';

const PageServiceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 240px;
  margin: 0 auto;
  ${mq({
    gap: ['36px', '24px'],
    maxWidth: ['100%', '580px'],
  })}
`;

const PageServiceButton = styled.button`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  outline: none;
  border-radius: 18px;
  position: absolute;
  right: 10px;
  top: 10px;
  bottom: 10px;
  color: ${COLORS.cloud};
  background: ${COLORS.tealPrimary};
`;

const PageServiceInput = styled.input`
  height: 56px;
  width: 100%;
  border: none;
  outline: none;
  border-radius: 28px;
  background: ${COLORS.grayBackground};
  padding: 24px;
  font-size: 18px;
  line-height: 28px;
  font-weight: 500;
`;

const PageServiceGroup = styled.div`
  position: relative;
  width: 100%;
`;

const PageServiceResult = styled.div<{
  serviced?: boolean;
}>`
  font-size: 18px;
  font-weight: 400;
  text-align: center;
  color: ${({ serviced }) => (serviced ? COLORS.tealPrimary : COLORS.toucan)};
`;

export const PageService: FC<{
  page: PageQuery['page'];
}> = ({ page }) => {
  if (!page) throw new Error('missing page');

  const [zip, setZIP] = useState<string>('');
  const [resultZIP, setResultZIP] = useState<string>('');
  const [execute, { data, loading }] = useLazyQuery(GeocodeDocument);

  const onSubmit: FormEventHandler = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    await execute({
      variables: { zip },
    });

    setResultZIP(zip);
  };

  const serviced = data?.geocode?.eligibleForDoorToDoor ?? null;

  const title = page.serviceAreaTitle.trim();
  const subtitle = page.serviceAreaSubtitle?.trim();

  return (
    <PageContent>
      <form onSubmit={onSubmit}>
        <PageServiceContainer>
          <PageSectionTitle>{title}</PageSectionTitle>
          {subtitle && <PageSectionSubtitle>{subtitle}</PageSectionSubtitle>}

          <PageServiceGroup>
            <PageServiceInput
              placeholder="Enter zipcode"
              value={zip}
              onChange={(event) => {
                setZIP(event.target.value);
              }}
            />
            <PageServiceButton type="submit" disabled={loading}>
              <Arrow fillColor={COLORS.cloud} direction="right" />
            </PageServiceButton>
          </PageServiceGroup>

          {serviced !== null && (
            <PageServiceResult serviced={serviced}>
              {serviced ? (
                <>
                  Great news! We can pick up and deliver in the {resultZIP}{' '}
                  area.
                </>
              ) : (
                <>Sorry we don’t serve in the {resultZIP} area yet!</>
              )}
            </PageServiceResult>
          )}
        </PageServiceContainer>
      </form>
    </PageContent>
  );
};
